import { useWebsiteVersion } from '@shared/hooks/websiteVersion';
import { useEventWebsiteDesignV2SitesQuery } from './useEventWebsiteDesign.graphql.generated';
import type { WebsiteV2DesignSite } from './useEventWebsiteDesign.types';

type WebsiteV2DesignSiteResult = Readonly<{
  /** The current V2 site (`useWebsiteVersion`) with its design. Null for a V1 event, and a V2 event with no site this viewer may read. */
  site: WebsiteV2DesignSite | null;
  isLoading: boolean;
}>;

/**
 * The design of an event's current Website V2 site, for the hooks in this folder. `eventHandle` is `''` for none.
 */
export const useWebsiteV2DesignSite = (eventHandle: string, { ssr }: Readonly<{ ssr: boolean }>): WebsiteV2DesignSiteResult => {
  const version = useWebsiteVersion({ eventHandle: eventHandle || undefined, ssr, skip: !eventHandle });
  // Every site's design, read beside the site list rather than after it picks one, so a server render
  // waits on one round of requests, as it did when the site came inline with the event.
  const readsSites = !!eventHandle && version.isWebsiteV2 && !!version.eventId;
  const { data, loading } = useEventWebsiteDesignV2SitesQuery({
    variables: { eventId: version.eventId ?? '' },
    batchMode: 'fast',
    ssr,
    skip: !readsSites,
  });
  const siteId = version.site?.id;
  const site = (siteId && data?.carbonWebsiteSitesByEventId.find(candidate => candidate.id === siteId)) || null;
  return { site, isLoading: version.isLoading || (readsSites && loading && !data) };
};
